import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  Input, Select, Form, Switch, Divider, 
  Button, List, Typography, Space, Row, Col  
} from "antd"; 
import { debounce } from "lodash";
import { getRegions } from "../../../redux/slices/regions/regionsThunk";
import { getSpecialtysList } from "../../../redux/slices/specialtys/specialtysThunk";
import { getGovernments } from "../../../redux/slices/governments/governmentsThunk";
import { StarFilled, DeleteOutlined, PlusOutlined } from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

const FormFields = ({ form, onAddPartnerNote, onDeletePartnerNote }) => {
  const { specialtysList, governments, regions, loading } = useSelector(
    (state) => ({
      specialtysList: state.specialtys.specialtys,
      governments: state.governments.governments,
      regions: state.regions.regions,
      loading: {
        persons: state.persons.loading,
        specialtys: state.specialtys.loading,
        governments: state.governments.loading,
        regions: state.regions.loading,
      },
    })
  );

  const [search, setSearch] = useState({
    government: "",
    region: "",
    specialty: "",
  });
  const [ids, setIds] = useState({
    government: "",
    region: "",
    specialty: "",
  });

  const dispatch = useDispatch();

  const debounceSearch = useCallback(
    debounce((value, key) => {
      setSearch((prevSearch) => ({
        ...prevSearch,
        [key]: value,
      }));
    }, 300),
    []
  );

  useEffect(() => {
    dispatch(getGovernments({ page: 1, limit: 50, search: search.government }));
  }, [search.government, dispatch]);

  useEffect(() => {
    const selectedGovernmentId = ids.government || null;
    dispatch(
      getRegions({
        page: 1,
        itemsPerPage: 50,
        governmentId: selectedGovernmentId,
        search: search.region || "",
      })
    );
  }, [ids.government, search.region, dispatch]);

  useEffect(() => {
    dispatch(
      getSpecialtysList({
        page: 1,
        itemsPerPage: 50,
        filter: { search: search.specialty },
      })
    );
  }, [search.specialty, dispatch]);

  const handleSearchedValue = (value, key) => {
    debounceSearch(value, key);
  };

  const handleSelect = (key, id) => {
    setIds((prevId) => ({
      ...prevId,
      [key]: id,
    }));
  };

  return (
    <div>
      {loading.persons && <p>Chargement...</p>}

      {[
        { name: "firstname", label: "Nom", placeholder: "Nom" },
        { name: "lastname", label: "Prénom", placeholder: "Prénom" },
        { name: "email", label: "Email", placeholder: "Email" },
        { name: "phone", label: "Téléphone", placeholder: "Téléphone" },
        { name: "address", label: "Adresse", placeholder: "Adresse" },
      ].map(({ name, label, placeholder }) => (
        <Form.Item
          key={name}
          name={name}
          label={label}
          rules={[
            { required: true, message: `Veuillez entrer ${label.toLowerCase()}` },
          ]}
        >
          <Input placeholder={placeholder} />
        </Form.Item>
      ))}

      {/* --- NOUVEAUX CHAMPS GPS (JUSTE APRÈS L'ADRESSE) --- */}
      <Form.Item label="Coordonnées GPS">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="latitude"
              rules={[{ pattern: /^-?\d+([.,]\d+)?$/, message: "Format : 36.8065" }]}
            >
              <Input placeholder="Latitude (ex: 36.8065)" allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="longitude"
              rules={[{ pattern: /^-?\d+([.,]\d+)?$/, message: "Format : 10.1815" }]}
            >
              <Input placeholder="Longitude (ex: 10.1815)" allowClear />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>

      <Form.Item
        name="potential"
        label="Potentiel"
        rules={[{ required: false }]}
      >
        <Select placeholder="Choisir le potentiel">
          <Option value="A+">A+</Option>
          <Option value="A">A</Option>
          <Option value="B+">B+</Option>
          <Option value="B">B</Option>
          <Option value="C">C</Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="specialty"
        label="Spécialité"
        rules={[{ required: true, message: "Veuillez sélectionner une spécialité" }]}
      >
        <Select
          placeholder={loading.specialtys ? "Chargement..." : "Choisir une spécialité"}
          loading={loading.specialtys}
          showSearch
          onSearch={(e) => handleSearchedValue(e, "specialty")}
          onSelect={(e) => handleSelect("specialty", e)}
          filterOption={false}
        >
          {specialtysList?.data?.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="government"
        label="Gouvernorat"
        rules={[{ required: true, message: "Veuillez sélectionner le gouvernorat" }]}
      >
        <Select
          placeholder={loading.governments ? "Chargement..." : "Choisir un gouvernorat"}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          loading={loading.governments}
        >
          {governments?.data?.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="region"
        label="Région"
        rules={[{ required: true, message: "Veuillez sélectionner une région" }]}
      >
        <Select
          placeholder={loading.regions ? "Chargement..." : "Choisir une région"}
          loading={loading.regions}
          showSearch
          onSearch={(e) => handleSearchedValue(e, "region")}
          onSelect={(e) => handleSelect("region", e)}
          filterOption={false}
        >
          {regions?.data?.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="doctorType"
        label="Type"
        rules={[{ required: true, message: "Veuillez sélectionner un type" }]}
      >
        <Select placeholder="Choisir un type">
          <Option value={2}>Privé</Option>
          <Option value={1}>Hospitalier</Option>
        </Select>
      </Form.Item>

      {/* --- SECTION PARTENAIRE --- */}
      <Divider orientation="left" style={{ borderColor: '#fadb14', color: '#d4b106' }}>
        <StarFilled /> Statut Partenaire
      </Divider>

      <Form.Item name="isPartner" label="Médecin Partenaire" valuePropName="checked">
        <Switch checkedChildren="OUI" unCheckedChildren="NON" />
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.isPartner !== curr.isPartner || prev.partnerNotes !== curr.partnerNotes}>
        {({ getFieldValue }) => 
          getFieldValue('isPartner') ? (
            <div style={{ marginLeft: '33%', marginBottom: '20px', padding: '15px', background: '#fffbe6', borderRadius: '8px', border: '1px solid #ffe58f' }}>
              <Text strong style={{ color: '#856404' }}>Historique Partenaire :</Text>
              
              <List
                size="small"
                style={{ marginTop: '10px', background: '#fff' }}
                bordered
                dataSource={getFieldValue('partnerNotes') ? getFieldValue('partnerNotes').split('\n').filter(Boolean) : []}
                renderItem={(item, index) => (
                  <List.Item actions={[
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onDeletePartnerNote(index)} />
                  ]}>
                    {item}
                  </List.Item>
                )}
              />

              <Space.Compact style={{ width: '100%', marginTop: '10px' }}>
                <Input 
                  id="newPartnerNoteInput" 
                  placeholder="Ajouter (ex: Voyage Allemagne 2025)" 
                  onPressEnter={(e) => { e.preventDefault(); onAddPartnerNote(); }}
                />
                <Button type="primary" onClick={onAddPartnerNote} icon={<PlusOutlined />}>
                  Ajouter
                </Button>
              </Space.Compact>
              
              <Form.Item name="partnerNotes" hidden><Input /></Form.Item>
            </div>
          ) : null
        }
      </Form.Item>
    </div>
  );
};

export default FormFields;